import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import useMovieData from '@jwp/ott-hooks-react/src/useMovieData';
import useTrailerData from '@jwp/ott-hooks-react/src/useTrailerData';
import useMovieParams from '@jwp/ott-ui-react/src/hooks/useMovieParams';
import useMovieHandlers from '@jwp/ott-ui-react/src/hooks/useMovieHandlers';
import useAutoFocus from '@jwp/ott-ui-react/src/hooks/useAutoFocus';
import type { ScreenComponent } from '@jwp/ott-ui-react/types/screens';
import Cinema from '@jwp/ott-ui-react/src/containers/Cinema/Cinema';
import TrailerModal from '@jwp/ott-ui-react/src/containers/TrailerModal/TrailerModal';
import ShareButton from '@jwp/ott-ui-react/src/components/ShareButton/ShareButton';
import FavoriteButton from '@jwp/ott-ui-react/src/containers/FavoriteButton/FavoriteButton';
import MediaButtonTrailer from '@jwp/ott-ui-react/src/components/MediaButtonTrailer/MediaButtonTrailer';
import VideoMetaData from '@jwp/ott-ui-react/src/components/VideoMetaData/VideoMetaData';
import HeroDescription from '@jwp/ott-ui-react/src/components/Hero/HeroDescription';
import MediaHead from '@jwp/ott-ui-react/src/components/MediaHead/MediaHead';
import MediaHeroInfo from '@jwp/ott-ui-react/src/components/MediaHeroInfo/MediaHeroInfo';
import MediaHeroButtons from '@jwp/ott-ui-react/src/components/MediaHeroButtons/MediaHeroButtons';
import MediaHero from '@jwp/ott-ui-react/src/components/MediaHero/MediaHero';
import StartWatchingButton from '@jwp/ott-ui-react/src/containers/StartWatchingButton/StartWatchingButton';
import { generateMovieJSONLD } from '@jwp/ott-common/src/utils/structuredData';
import env from '@jwp/ott-common/src/env';
import MediaSection from '@jwp/ott-ui-react/src/components/MediaSection/MediaSection';
import CardGrid from '@jwp/ott-ui-react/src/components/CardGrid/CardGrid';

const MediaVideoCinema: ScreenComponent<PlaylistItem> = ({ data, isLoading }) => {
  const { t } = useTranslation('video');
  const { id, play, feedId } = useMovieParams();
  const {
    accessModel,
    isLoggedIn,
    hasSubscription,
    passes,
    playlist,
    playlistTitle,
    nextItem,
    playUrl,
    movieURL,
    isFavoritesEnabled,
    isEntitled,
    primaryMetadata,
    isPlaylistLoading,
    getCardUrl,
  } = useMovieData(data, id, feedId);
  const { hasTrailer, trailerItem, trailerTitle, isTrailerPlaying, playTrailer, closeTrailer } = useTrailerData(data);
  const { handleBack, handleComplete } = useMovieHandlers(nextItem, movieURL, playlist);
  const structuredData = generateMovieJSONLD(data, env.APP_PUBLIC_URL);
  const canonicalUrl = data ? `${env.APP_PUBLIC_URL}${mediaURL({ id: data.mediaid, title: data.title })}` : window.location.href;

  useAutoFocus(id);

  return (
    <React.Fragment>
      <MediaHead canonicalUrl={canonicalUrl} data={data} structuredData={structuredData} />
      <MediaHero image={data.backgroundImage}>
        <MediaHeroInfo
          title={data.title}
          description={<HeroDescription description={data.description} />}
          metadataComponent={<VideoMetaData attributes={primaryMetadata} />}
          tagsComponent={null}
          definitionComponent={null}
        />
        <MediaHeroButtons
          buttonClassOverride={false}
          cta={
            <StartWatchingButton
              key={id} // necessary to fix autofocus on TalkBack
              item={data}
              playUrl={playUrl}
            />
          }
        >
          <MediaButtonTrailer hasTrailer={hasTrailer} trailerItem={trailerItem} isTrailerPlaying={isTrailerPlaying} onClick={playTrailer} />
          {isFavoritesEnabled ? <FavoriteButton item={data} /> : undefined}
          <ShareButton title={data.title} description={data.description} url={canonicalUrl} />
        </MediaHeroButtons>
      </MediaHero>
      {playlist && (
        <MediaSection title={playlistTitle || '\u00A0'}>
          <CardGrid
            playlist={playlist}
            isLoading={isLoading || isPlaylistLoading}
            accessModel={accessModel}
            isLoggedIn={isLoggedIn}
            currentCardItem={data}
            currentCardLabel={t('video:current_video')}
            hasSubscription={hasSubscription}
            passes={passes}
            getUrl={getCardUrl}
          />
        </MediaSection>
      )}
      <Cinema
        open={play && isEntitled}
        onClose={handleBack}
        item={data}
        title={data.title}
        primaryMetadata={<VideoMetaData attributes={primaryMetadata} />}
        onComplete={handleComplete}
        feedId={feedId ?? undefined}
        onNext={handleComplete}
      />
      <TrailerModal item={trailerItem} title={trailerTitle} open={isTrailerPlaying} onClose={closeTrailer} />
    </React.Fragment>
  );
};

export default MediaVideoCinema;
